import type { z } from "zod";

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * Set once `/api/me` has resolved in this tab, so a later 401 can tell "the session that was
 * here just ended" from "never signed in" — the sign-in screen shows a different reason for each.
 */
let hadSession = false;

/** Fetch JSON from the same-origin API and validate it against a contract schema. */
export async function api<T>(path: string, schema: z.ZodType<T>, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: {
      accept: "application/json",
      ...(init?.body ? { "content-type": "application/json" } : {}),
    },
    ...init,
  });
  if (res.ok && path === "/api/me") hadSession = true;
  if (res.status === 401 && !location.pathname.startsWith("/sign-in")) {
    const next = location.pathname + location.search;
    // An invitation link keeps its token through the round trip, so sign-in can name the
    // inviter and the callback can land back on /invite/<token>.
    const invited = /^\/invite\/([^/?#]+)/.exec(location.pathname);
    const invite = invited?.[1] ? `invite=${encodeURIComponent(invited[1])}&` : "";
    // A session that was here before ending mid-visit gets the "expired" notice; a first
    // visit with no session yet goes straight to plain sign-in.
    const reason = hadSession ? "reason=expired&" : "";
    location.assign(`/sign-in?${invite}${reason}next=${encodeURIComponent(next)}`);
  }
  if (!res.ok) {
    let message = res.statusText;
    try {
      const body = (await res.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {}
    throw new ApiError(res.status, message);
  }
  return schema.parse(await res.json());
}
